import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Phone,
  MessageSquare,
  Mail,
  UserPlus,
  Shield,
  AlertTriangle,
  MessageCircle,
  Calendar,
  CheckCircle,
  RefreshCw,
  Database,
  Bell,
  StickyNote,
  Repeat,
  AlertCircle,
  Send,
  Wallet,
  MapPin,
  Landmark,
  Clock,
  Languages,
  Loader2,
} from 'lucide-react';

import { useRole } from '../contexts/RoleContext.jsx';
import { useApiData } from '../hooks/useApiData.js';
import { useToast } from '../contexts/ToastContext.jsx';
import { ErrorBanner, InlineSpinner } from '../components/common/PageState.jsx';
import VerificationBrief from '../components/VerificationBrief.jsx';
import { api, assignLead, getLead, shouldUseApi } from '../services/api.js';
import {
  leadById,
  agents,
  agentById,
  verificationByLeadId,
  conversationsByLeadId,
  activitiesByLeadId,
  showingsByLeadId,
  propertyById,
  NOW_ISO,
} from '../data/seedData.js';
import {
  DEMO_BRIEF_VOLKOV,
  DEMO_BRIEF_JAMES_CHEN,
  DEMO_BRIEF_DAVID_RODRIGUEZ,
  DEMO_BRIEF_ROBERTO_SILVA,
  DEMO_BRIEF_SARAH_WILLIAMS,
  DEMO_BRIEF_SOFIA_MARTINEZ,
  DEMO_BRIEF_PATRICIA_DUBOIS,
  DEMO_BRIEF_CARLOS_MENDEZ,
  DEMO_BRIEF_MARIA_LOPEZ,
  DEMO_BRIEF_EMILY_WATSON,
  DEMO_BRIEF_THOMAS_BROWN,
  DEMO_BRIEF_MICHAEL_PARK,
  DEMO_BRIEF_ANA_GARCIA,
  DEMO_BRIEF_FRANK_REED,
  DEMO_BRIEF_KEVIN_HUGHES,
  DEMO_BRIEF_JENNIFER_DAVIS,
  DEMO_BRIEF_LINDA_BROOKS,
  DEMO_BRIEF_RICHARD_SMITH,
} from '../data/demoBriefs.js';
import { humanizeToken, humanizeList } from '../utils/humanize.js';

// Fallback briefs keyed by the demo lead's email. Used when the backend hasn't
// been migrated yet (demo_brief column still null) — the DB value always wins
// when present so a future brief update doesn't have to ship through the app.
const DEMO_BRIEF_BY_EMAIL = {
  'alex.volkov@demo.example': DEMO_BRIEF_VOLKOV,
  'james.chen@demo.example': DEMO_BRIEF_JAMES_CHEN,
  'david.rodriguez@demo.example': DEMO_BRIEF_DAVID_RODRIGUEZ,
  'roberto.silva@demo.example': DEMO_BRIEF_ROBERTO_SILVA,
  'sarah.williams@demo.example': DEMO_BRIEF_SARAH_WILLIAMS,
  'sofia.martinez@demo.example': DEMO_BRIEF_SOFIA_MARTINEZ,
  'patricia.dubois@demo.example': DEMO_BRIEF_PATRICIA_DUBOIS,
  'carlos.mendez@demo.example': DEMO_BRIEF_CARLOS_MENDEZ,
  'maria.lopez@demo.example': DEMO_BRIEF_MARIA_LOPEZ,
  'emily.watson@demo.example': DEMO_BRIEF_EMILY_WATSON,
  'thomas.brown@demo.example': DEMO_BRIEF_THOMAS_BROWN,
  'michael.park@demo.example': DEMO_BRIEF_MICHAEL_PARK,
  'ana.garcia@demo.example': DEMO_BRIEF_ANA_GARCIA,
  'frank.reed@demo.example': DEMO_BRIEF_FRANK_REED,
  'kevin.hughes@demo.example': DEMO_BRIEF_KEVIN_HUGHES,
  'jennifer.davis@demo.example': DEMO_BRIEF_JENNIFER_DAVIS,
  'linda.brooks@demo.example': DEMO_BRIEF_LINDA_BROOKS,
  'richard.smith@demo.example': DEMO_BRIEF_RICHARD_SMITH,
};

function getDemoLeadBrief(lead) {
  if (!lead) return null;
  if (lead.demoBrief) return lead.demoBrief;
  if (lead.demo_brief) return lead.demo_brief;
  const email = (lead.email || '').toLowerCase();
  return DEMO_BRIEF_BY_EMAIL[email] || null;
}

const NOW = new Date(NOW_ISO);
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

// ── helpers ────────────────────────────────────────────────────────────────

function relativeTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso);
  const diffMin = Math.round((NOW - then) / 60_000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD === 1) return 'Yesterday';
  if (diffD < 7) return `${diffD} days ago`;
  if (diffD < 30) return `${Math.round(diffD / 7)}w ago`;
  if (diffD < 365) return `${Math.round(diffD / 30)}mo ago`;
  return `${Math.round(diffD / 365)}y ago`;
}

function formatClock(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
  });
}

function formatMoney(n) {
  if (n == null) return '—';
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `$${m.toFixed(m % 1 === 0 ? 0 : 1)}M`;
  }
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
}

function formatBudget(b) {
  if (!b) return '—';
  return `${formatMoney(b.min)} – ${formatMoney(b.max)}`;
}

function scoreColorClass(score) {
  if (score >= 80) return 'text-score-green';
  if (score >= 50) return 'text-score-yellow';
  return 'text-score-red';
}

const TEMP_BADGE = {
  hot: { label: 'Hot', cls: 'bg-hot/15 text-hot' },
  warm: { label: 'Warm', cls: 'bg-warm/15 text-warm' },
  nurture: { label: 'Nurture', cls: 'bg-nurture/15 text-nurture' },
  cold: { label: 'Cold', cls: 'bg-cold/15 text-cold' },
  dead: { label: 'Dead', cls: 'bg-cold/15 text-cold' },
  red_flag: { label: 'Red Flag', cls: 'bg-hot/20 text-hot' },
};

const STATUS_LABEL = {
  new: 'New',
  qualifying: 'Qualifying',
  qualified: 'Qualified',
  verified: 'Verified',
  showing: 'Showing',
  nurture: 'Nurture',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
  disqualified: 'Disqualified',
  dead: 'Dead',
};

const SOURCE_META = {
  zillow: { label: 'Zillow', cls: 'bg-blue-500/15 text-blue-400' },
  facebook_ads: { label: 'Facebook', cls: 'bg-indigo-500/15 text-indigo-400' },
  instagram: { label: 'Instagram', cls: 'bg-pink-500/15 text-pink-400' },
  whatsapp: { label: 'WhatsApp', cls: 'bg-verified/15 text-verified' },
  website: { label: 'Website', cls: 'bg-accent/15 text-accent' },
  referral: { label: 'Referral', cls: 'bg-warm/15 text-warm' },
  manual: { label: 'Manual', cls: 'bg-cold/15 text-cold' },
  sms: { label: 'SMS', cls: 'bg-nurture/15 text-nurture' },
};

function sourceMeta(src) {
  return SOURCE_META[src] || { label: src, cls: 'bg-cold/15 text-cold' };
}

const CHANNEL_LABEL = {
  webchat: 'Webchat',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  voice: 'Voice',
  email: 'Email',
};

const LANG_LABEL = {
  en: 'EN',
  es: 'ES',
  pt: 'PT',
};

// Prefer the verification attached to the lead by the API/normalizer;
// fall back to the seed lookup for demo mode.
function leadVerification(lead) {
  if (!lead) return null;
  if (lead.verification) return lead.verification;
  return verificationByLeadId[lead.id] || null;
}

const ACTIVITY_META = {
  ai_conversation: { icon: MessageCircle, cls: 'text-accent bg-accent/10' },
  verification: { icon: Shield, cls: 'text-verified bg-verified/10' },
  showing_booked: { icon: Calendar, cls: 'text-nurture bg-nurture/10' },
  showing_completed: { icon: CheckCircle, cls: 'text-verified bg-verified/10' },
  agent_call: { icon: Phone, cls: 'text-nurture bg-nurture/10' },
  agent_note: { icon: StickyNote, cls: 'text-warm bg-warm/10' },
  status_change: { icon: RefreshCw, cls: 'text-text-secondary bg-bg-subtle' },
  enrichment: { icon: Database, cls: 'text-accent bg-accent/10' },
  assignment: { icon: UserPlus, cls: 'text-nurture bg-nurture/10' },
  sms_alert: { icon: AlertCircle, cls: 'text-hot bg-hot/10' },
  reminder: { icon: Bell, cls: 'text-warm bg-warm/10' },
  followup: { icon: Repeat, cls: 'text-accent bg-accent/10' },
};

function activityMeta(type) {
  return ACTIVITY_META[type] || { icon: MessageCircle, cls: 'text-text-secondary bg-bg-subtle' };
}

const SHOWING_STATUS_BADGE = {
  scheduled: { label: 'Scheduled', cls: 'bg-nurture/15 text-nurture' },
  confirmed: { label: 'Confirmed', cls: 'bg-verified/15 text-verified' },
  completed: { label: 'Completed', cls: 'bg-verified/15 text-verified' },
  needs_confirmation: { label: 'Needs Confirmation', cls: 'bg-warm/15 text-warm' },
  no_show: { label: 'No Show', cls: 'bg-hot/15 text-hot' },
  cancelled: { label: 'Cancelled', cls: 'bg-cold/15 text-cold' },
};

// ── subcomponents ──────────────────────────────────────────────────────────

function Badge({ className = '', children }) {
  return <span className={`badge ${className}`}>{children}</span>;
}

function SectionHeader({ title, right = null }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-4">
      <h2 className="text-base md:text-lg font-semibold text-text-primary">{title}</h2>
      {right}
    </div>
  );
}

function ContactHeader({ lead, onAssign, canAssign }) {
  const temp = TEMP_BADGE[lead.temperature] || TEMP_BADGE.cold;
  const statusLabel = STATUS_LABEL[lead.status] || lead.status;
  const src = sourceMeta(lead.source);
  // Seed roster first (demo mode — assignedAgentId is a seed string), then
  // the nested agent object the API attaches via normalizeLead.
  const assigned =
    (lead.assignedAgentId && agentById[lead.assignedAgentId]) ||
    (lead.agent && lead.agent.name ? lead.agent : null);
  const telHref = lead.phone ? `tel:${lead.phone.replace(/[^\d+]/g, '')}` : null;
  const smsHref = lead.phone ? `sms:${lead.phone.replace(/[^\d+]/g, '')}` : null;
  const mailtoHref = lead.email ? `mailto:${lead.email}` : null;
  const [assignOpen, setAssignOpen] = useState(false);

  return (
    <div className="card p-5 md:p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-[28px] font-bold text-text-primary leading-tight">
            {lead.name}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Badge className={temp.cls}>{temp.label}</Badge>
            <Badge className="bg-bg-subtle border border-border-card text-text-secondary">
              {statusLabel}
            </Badge>
            <Badge className={src.cls}>{src.label}</Badge>
            <Badge className="bg-bg-subtle border border-border-card text-text-secondary flex items-center gap-1">
              <Languages size={11} />
              {LANG_LABEL[lead.preferredLanguage] || lead.preferredLanguage?.toUpperCase() || '—'}
            </Badge>
          </div>
        </div>

        {/* Large score pill */}
        <div
          className={`shrink-0 rounded-full border ${
            lead.score >= 80
              ? 'border-score-green/50 bg-score-green/10'
              : lead.score >= 50
                ? 'border-score-yellow/50 bg-score-yellow/10'
                : 'border-score-red/50 bg-score-red/10'
          } w-20 h-20 md:w-24 md:h-24 flex flex-col items-center justify-center`}
        >
          <span className={`text-3xl md:text-4xl font-bold tabular-nums ${scoreColorClass(lead.score)}`}>
            {lead.score}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-text-secondary">
            Score
          </span>
        </div>
      </div>

      {/* Contact info grid */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        {lead.phone && (
          <a
            href={telHref}
            className="flex items-center gap-2 text-text-secondary hover:text-accent"
          >
            <Phone size={14} className="shrink-0" />
            <span className="truncate">{lead.phone}</span>
          </a>
        )}
        {lead.email && (
          <a
            href={mailtoHref}
            className="flex items-center gap-2 text-text-secondary hover:text-accent"
          >
            <Mail size={14} className="shrink-0" />
            <span className="truncate">{lead.email}</span>
          </a>
        )}
        <div className="flex items-center gap-2 text-text-secondary">
          <Clock size={14} className="shrink-0" />
          <span>
            Timeline:{' '}
            <span className="text-text-primary">
              {lead.timeline ? humanizeToken(lead.timeline) : '—'}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-2 text-text-secondary">
          <Wallet size={14} className="shrink-0" />
          <span>
            Budget: <span className="text-text-primary">{formatBudget(lead.budget)}</span>
          </span>
        </div>
        <div className="flex items-center gap-2 text-text-secondary">
          <Landmark size={14} className="shrink-0" />
          <span>
            Financing:{' '}
            <span className="text-text-primary">
              {lead.financingType ? humanizeToken(lead.financingType) : '—'}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-2 text-text-secondary">
          <MapPin size={14} className="shrink-0" />
          <span className="truncate">
            {humanizeList(lead.neighborhoods) || '—'}
          </span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="mt-5 flex flex-wrap items-stretch gap-2">
        <a
          href={telHref || '#'}
          className="inline-flex items-center gap-2 rounded-pill bg-verified hover:bg-[#16a34a] text-white
                     px-5 py-2.5 text-sm font-semibold transition-colors"
        >
          <Phone size={15} /> Call
        </a>
        <a
          href={smsHref || '#'}
          className="inline-flex items-center gap-2 rounded-pill bg-nurture hover:bg-[#2563eb] text-white
                     px-5 py-2.5 text-sm font-semibold transition-colors"
        >
          <MessageSquare size={15} /> Text
        </a>
        <a
          href={mailtoHref || '#'}
          className="inline-flex items-center gap-2 rounded-outlined border-2 border-border-card bg-white
                     hover:border-text-muted text-text-primary px-5 py-2.5 text-sm font-semibold transition-colors"
        >
          <Mail size={15} /> Email
        </a>

        {canAssign ? (
        <div className="relative">
          <button
            type="button"
            onClick={() => setAssignOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-pill bg-white border-2 border-accent text-accent
                       hover:bg-accent/[0.06] px-5 py-2.5 text-sm font-semibold transition-colors"
          >
            <UserPlus size={15} />
            {assigned ? `Assigned: ${assigned.name.split(' ')[0]}` : 'Assign Agent'}
          </button>
          {assignOpen && (
            <div
              className="absolute right-0 mt-2 w-56 card p-1 z-10 shadow-lg"
              onMouseLeave={() => setAssignOpen(false)}
            >
              <button
                type="button"
                onClick={() => {
                  onAssign(null);
                  setAssignOpen(false);
                }}
                className="block w-full text-left px-3 py-2 text-sm text-text-secondary
                           hover:bg-bg-subtle rounded-btn"
              >
                Unassign
              </button>
              {agents.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    onAssign(a.id);
                    setAssignOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-2 text-sm rounded-btn
                             hover:bg-accent/10 ${
                               a.id === lead.assignedAgentId
                                 ? 'text-accent'
                                 : 'text-text-primary'
                             }`}
                >
                  <span className="font-medium">{a.name}</span>
                  <span className="block text-xs text-text-secondary">{a.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        ) : assigned ? (
          <div className="inline-flex items-center gap-2 rounded-outlined bg-bg-subtle border border-border-card
                          px-4 py-2.5 text-sm text-text-secondary">
            <UserPlus size={15} className="text-accent" />
            <span>
              Assigned to{' '}
              <span className="text-text-primary font-medium">{assigned.name}</span>
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Deterministic demo brief for seeded leads — keeps the demo fast and
// doesn't burn Apollo credits when we're not in live API mode. Volkov's
// shape is preserved (low score, honest "can't verify" copy) per the
// build spec's red-flag example.
function _seedBriefFor(lead) {
  const name = lead.name || 'this buyer';
  const v = leadVerification(lead);
  const score = v?.overallScore ?? lead.score ?? 50;
  const tier =
    score >= 80
      ? 'LIKELY_REAL'
      : score >= 60
        ? 'MOSTLY_REAL'
        : score >= 30
          ? 'CANT_VERIFY'
          : 'LIKELY_FAKE';

  const evidence = Object.values(v?.dimensions || {})
    .map((d) => d?.evidence)
    .filter(Boolean);
  const flagMessages = (v?.flags || [])
    .map((f) => f?.message)
    .filter(Boolean);

  return {
    score,
    tier,
    verdict_line:
      tier === 'LIKELY_REAL'
        ? `I looked into ${name} and everything I found lines up — they look like a real, qualified buyer.`
        : tier === 'MOSTLY_REAL'
          ? `I looked into ${name} and most of what they told you checks out — a few gaps, but nothing alarming.`
          : tier === 'CANT_VERIFY'
            ? `I looked into ${name} and couldn't confirm enough to make a confident call.`
            : `I looked into ${name} and the picture doesn't add up — several of their claims don't hold up.`,
    checks_out: tier === 'LIKELY_FAKE' ? [] : evidence.slice(0, 3),
    cant_confirm: tier === 'LIKELY_REAL' ? [] : flagMessages.slice(0, 3),
    worth_knowing: [
      lead.budget?.max
        ? `Stated budget around $${Math.round(lead.budget.max / 1000)}K.`
        : null,
      Array.isArray(lead.neighborhoods) && lead.neighborhoods.length
        ? `Focus: ${lead.neighborhoods.slice(0, 2).join(', ')}.`
        : null,
      lead.preApprovalLender ? `Lender: ${lead.preApprovalLender}.` : null,
    ].filter(Boolean),
    my_take:
      tier === 'LIKELY_REAL'
        ? 'Proceed — this one is worth your time.'
        : tier === 'MOSTLY_REAL'
          ? 'Proceed with normal due diligence.'
          : tier === 'CANT_VERIFY'
            ? 'Proceed with caution — get their email and a pre-approval before booking a showing.'
            : 'Decline for now — too many gaps to justify time on them.',
    crm_sync_message:
      "I've already updated your CRM with what I found — their contact details and my notes are synced.",
  };
}

function LeadVerificationBrief({ lead }) {
  const [state, setState] = useState({ loading: false, brief: null, error: null });

  // Reset when navigating between leads.
  useEffect(() => {
    setState({ loading: false, brief: null, error: null });
  }, [lead.id]);

  // Hardcoded demo briefs render verbatim — no backend round-trip. The DB
  // value (demo_brief / demoBrief on the lead) wins if present; otherwise we
  // fall back to the frontend-bundled brief matched by email, so the demo
  // works even when the environment hasn't run migration 0015 yet.
  const hardcoded = getDemoLeadBrief(lead);
  if (hardcoded) {
    return <VerificationBrief brief={hardcoded} name={lead.name} />;
  }

  const runVerification = async () => {
    setState({ loading: true, brief: null, error: null });

    // Demo mode: derive from seed data instantly, no API cost.
    if (!shouldUseApi()) {
      setState({ loading: false, brief: _seedBriefFor(lead), error: null });
      return;
    }

    try {
      const fd = new FormData();
      fd.append('name', lead.name);
      if (lead.email) fd.append('email', lead.email);
      if (lead.phone) fd.append('phone', lead.phone);
      if (lead.budget?.max) fd.append('budget', String(lead.budget.max));
      if (Array.isArray(lead.neighborhoods) && lead.neighborhoods.length) {
        fd.append('stated_location', lead.neighborhoods.join(', '));
      }
      if (lead.preApprovalLender) fd.append('lender', lead.preApprovalLender);
      if (lead.notes) fd.append('notes', lead.notes);

      const resp = await api.post('/verification/enrich', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90_000,
      });
      setState({ loading: false, brief: resp.data?.brief || null, error: null });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[lead-detail] verification brief failed', err);
      setState({
        loading: false,
        brief: _seedBriefFor(lead),
        error:
          err?.response?.data?.detail ||
          err?.message ||
          'Could not reach verification service',
      });
    }
  };

  if (state.loading) {
    return (
      <div className="card p-6 flex items-center gap-3">
        <Loader2 size={18} className="animate-spin text-accent" />
        <span className="text-sm text-text-secondary">
          Lisa is putting together a brief…
        </span>
      </div>
    );
  }

  if (state.brief) {
    return (
      <div className="space-y-3">
        {state.error && (
          <div className="rounded-btn border border-warm/40 bg-warm/10 px-3 py-2 text-xs text-warm flex items-center gap-2">
            <AlertTriangle size={13} />
            Showing a cached brief — couldn't reach the verification service.
          </div>
        )}
        <VerificationBrief brief={state.brief} name={lead.name} />
      </div>
    );
  }

  return (
    <div className="card p-8 flex flex-col items-center text-center gap-4">
      <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
        <Shield size={22} className="text-accent" />
      </div>
      <div>
        <h3 className="text-base font-semibold text-text-primary">No verification yet</h3>
        <p className="mt-1 text-sm text-text-secondary max-w-xs">
          Lisa hasn't run verification on this lead yet. Click below when you're ready.
        </p>
      </div>
      <button
        type="button"
        onClick={runVerification}
        className="inline-flex items-center gap-2 rounded-pill bg-accent hover:bg-accent-hover
                   text-white px-6 py-2.5 text-sm font-semibold transition-colors"
      >
        <Shield size={15} /> Run Verification
      </button>
      <p className="text-[11px] text-text-secondary">
        Running verification uses 1 credit from your plan
      </p>
    </div>
  );
}

function ConversationPanel({ lead }) {
  // API attaches conversations onto the lead when available; otherwise fall
  // back to the seed fixture keyed by the seed lead id.
  const conversations =
    (Array.isArray(lead.conversations) && lead.conversations.length
      ? lead.conversations
      : conversationsByLeadId[lead.id]) || [];
  const conv = conversations[conversations.length - 1]; // most recent

  return (
    <div className="card p-5">
      <SectionHeader
        title="AI Conversation"
        right={
          conv ? (
            <div className="flex items-center gap-2">
              <Badge className="bg-accent/15 text-accent">
                {CHANNEL_LABEL[conv.channel] || conv.channel}
              </Badge>
              <Badge className="bg-bg-subtle border border-border-card text-text-secondary">
                {LANG_LABEL[conv.language] || conv.language?.toUpperCase()}
              </Badge>
            </div>
          ) : null
        }
      />

      {!conv ? (
        <p className="text-sm text-text-secondary">No conversation recorded.</p>
      ) : (
        <>
          {conv.escalation && (
            <div className="mb-3 rounded-btn border border-warm/40 bg-warm/10 px-3 py-2 text-xs text-warm">
              <div className="font-semibold flex items-center gap-1">
                <AlertTriangle size={12} /> Escalated to human
              </div>
              <div className="mt-0.5 text-text-secondary">{conv.escalation.trigger}</div>
            </div>
          )}
          <div className="max-h-[420px] overflow-y-auto pr-1 space-y-3">
            {conv.messages.map((m, i) => {
              const isLead = m.from === 'lead';
              const isAgent = m.from === 'agent';
              return (
                <div
                  key={i}
                  className={`flex ${isLead ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-card px-3 py-2 text-sm ${
                      isLead
                        ? 'bg-bg-subtle text-text-primary'
                        : isAgent
                          ? 'bg-verified/10 text-text-primary border-l-2 border-verified'
                          : 'bg-accent/[0.08] text-text-primary border-l-2 border-accent'
                    }`}
                  >
                    {!isLead && (
                      <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">
                        {isAgent
                          ? (agentById[m.agentId]?.name || 'Agent')
                          : 'AI'}
                      </div>
                    )}
                    <div className="whitespace-pre-wrap leading-relaxed">{m.text}</div>
                    <div className="mt-1 text-[10px] text-text-secondary">
                      {relativeTime(m.at)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function ActivityTimeline({ lead }) {
  const source =
    Array.isArray(lead.activities) && lead.activities.length
      ? lead.activities
      : activitiesByLeadId[lead.id] || [];
  const items = source
    .slice()
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <div className="card p-5">
      <SectionHeader
        title="Activity Timeline"
        right={<span className="text-xs text-text-secondary">{items.length} events</span>}
      />
      {items.length === 0 ? (
        <p className="text-sm text-text-secondary">No activity recorded.</p>
      ) : (
        <ol className="relative space-y-5">
          {/* Vertical connector */}
          <span
            aria-hidden="true"
            className="absolute left-[14px] top-2 bottom-2 w-px bg-border-card"
          />
          {items.map((a) => {
            const meta = activityMeta(a.type);
            const Icon = meta.icon;
            const seedAgent = a.agentId ? agentById[a.agentId] : null;
            const agentName = seedAgent?.name || a.agentName || null;
            return (
              <li key={a.id} className="relative pl-10">
                <span
                  className={`absolute left-0 top-0 w-7 h-7 rounded-full flex items-center justify-center ${meta.cls}`}
                >
                  <Icon size={13} />
                </span>
                <div className="flex items-center gap-2 text-xs text-text-secondary">
                  <span>{relativeTime(a.createdAt)}</span>
                  <span className="opacity-40">·</span>
                  <span className="uppercase tracking-wider">
                    {humanizeToken(a.type)}
                  </span>
                  {agentName && (
                    <>
                      <span className="opacity-40">·</span>
                      <span>{agentName}</span>
                    </>
                  )}
                </div>
                <p className="mt-1 text-sm text-text-primary leading-relaxed">
                  {a.description}
                </p>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function ShowingHistory({ lead }) {
  const source =
    Array.isArray(lead.showings) && lead.showings.length
      ? lead.showings
      : showingsByLeadId[lead.id] || [];
  const list = source
    .slice()
    .sort((a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt));

  const upcoming = list.filter((s) => new Date(s.scheduledAt) >= NOW);
  const past = list.filter((s) => new Date(s.scheduledAt) < NOW);
  const ordered = [...upcoming.reverse(), ...past]; // upcoming ascending, then past

  return (
    <div className="card p-5">
      <SectionHeader
        title="Showings"
        right={<span className="text-xs text-text-secondary">{list.length}</span>}
      />
      {ordered.length === 0 ? (
        <p className="text-sm text-text-secondary">No showings booked.</p>
      ) : (
        <ul className="space-y-3">
          {ordered.map((s) => {
            const prop =
              (s.propertyId && propertyById[s.propertyId]) ||
              s.property ||
              null;
            const seedAgent = s.agentId ? agentById[s.agentId] : null;
            const agentName =
              seedAgent?.name || s.agent?.name || s.agentName || null;
            const badge = SHOWING_STATUS_BADGE[s.status] || {
              label: s.status,
              cls: 'bg-cold/15 text-cold',
            };
            const when = new Date(s.scheduledAt);
            const isPast = when < NOW;
            const address = prop?.address || s.propertyAddress || 'Property';
            return (
              <li
                key={s.id}
                className="rounded-card border border-border-card bg-bg-subtle p-3"
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-text-primary truncate">
                      {address}
                    </div>
                    <div className="mt-0.5 text-xs text-text-secondary">
                      {prop?.neighborhood ? `${prop.neighborhood} · ` : ''}
                      {formatClock(s.scheduledAt)}
                      {!isPast && (
                        <span className="ml-2 text-accent">
                          ({relativeTime(s.scheduledAt).replace(' ago', ' from now')})
                        </span>
                      )}
                    </div>
                    {agentName && (
                      <div className="mt-0.5 text-xs text-text-secondary">
                        with {agentName}
                      </div>
                    )}
                    {s.notes && (
                      <div className="mt-1 text-xs text-text-secondary italic">
                        {s.notes}
                      </div>
                    )}
                  </div>
                  <Badge className={badge.cls}>{badge.label}</Badge>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function NotesPanel({ lead }) {
  const allActivities =
    Array.isArray(lead.activities) && lead.activities.length
      ? lead.activities
      : activitiesByLeadId[lead.id] || [];
  const seedNotes = allActivities
    .filter((a) => a.type === 'agent_note')
    .map((a) => ({
      id: a.id,
      author:
        (a.agentId && agentById[a.agentId]?.name) ||
        a.agentName ||
        'System',
      at: a.createdAt,
      text: a.description,
    }));

  const [draft, setDraft] = useState('');
  const [extra, setExtra] = useState([]);

  const addNote = () => {
    const text = draft.trim();
    if (!text) return;
    setExtra((n) => [
      {
        id: `local-${Date.now()}`,
        author: 'You',
        at: new Date().toISOString(),
        text,
      },
      ...n,
    ]);
    setDraft('');
  };

  const all = [...extra, ...seedNotes];

  return (
    <div className="card p-5">
      <SectionHeader
        title="Notes"
        right={<span className="text-xs text-text-secondary">{all.length}</span>}
      />

      <div className="space-y-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a note for the team…"
          rows={3}
          className="w-full bg-white border border-border-card rounded-[2px] text-text-primary
                     placeholder:text-text-muted text-sm px-3 py-2
                     hover:border-accent/60 focus:border-accent focus:outline-none
                     focus:ring-1 focus:ring-accent/30 transition-colors"
        />
        <div className="flex justify-end">
          <button
            type="button"
            onClick={addNote}
            disabled={!draft.trim()}
            className="inline-flex items-center gap-1.5 rounded-pill bg-accent hover:bg-accent-hover
                       text-white px-4 py-1.5 text-sm font-semibold transition-colors
                       disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send size={13} /> Save note
          </button>
        </div>
      </div>

      {all.length > 0 && (
        <ul className="mt-4 space-y-3">
          {all.map((n) => (
            <li
              key={n.id}
              className="rounded-card border border-border-card bg-bg p-3"
            >
              <div className="flex items-center gap-2 text-xs text-text-secondary">
                <StickyNote size={12} />
                <span className="text-text-primary font-medium">{n.author}</span>
                <span>·</span>
                <span>{relativeTime(n.at)}</span>
              </div>
              <p className="mt-1 text-sm text-text-primary leading-relaxed">{n.text}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── page ───────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'verification', label: 'Verification' },
  { key: 'transcript', label: 'Transcript' },
  { key: 'activity', label: 'Activity' },
  { key: 'showings', label: 'Showings' },
];

export default function LeadDetail() {
  const { id } = useParams();
  const { role } = useRole();
  const { showToast } = useToast();
  const canAssign = role !== 'agent';
  const apiReq = useApiData(() => getLead(id), [id]);
  const lead = apiReq.data || leadById[id];
  const [activeTab, setActiveTab] = useState('overview');
  // Optimistic override that also fires the assign API so the change sticks.
  const [assignedOverride, setAssignedOverride] = useState(undefined);

  const effectiveLead = useMemo(() => {
    if (!lead) return null;
    if (assignedOverride === undefined) return lead;
    return { ...lead, assignedAgentId: assignedOverride };
  }, [lead, assignedOverride]);

  const handleAssign = async (agentId) => {
    setAssignedOverride(agentId);
    if (!agentId) {
      showToast('Agent unassigned', { type: 'info' });
      return;
    }
    try {
      await assignLead(id, agentId);
      const name = agentById[agentId]?.name;
      showToast(name ? `Assigned to ${name}` : 'Lead assigned', { type: 'success' });
    } catch (err) {
      if (err?.message !== 'assignLead requires live API') {
        showToast(err?.response?.data?.detail || 'Could not assign agent', {
          type: 'error',
        });
      }
    }
  };

  if (!lead) {
    return (
      <div className="space-y-4">
        <Link
          to="/leads"
          className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-accent"
        >
          <ArrowLeft size={14} /> Back to leads
        </Link>
        {apiReq.loading ? (
          <div className="card p-10 text-center">
            <InlineSpinner label="Loading lead…" />
          </div>
        ) : (
          <div className="card p-10 text-center">
            <p className="text-base text-text-primary font-medium">Lead not found</p>
            <p className="mt-1 text-sm text-text-secondary">
              The lead with id <code className="text-text-primary">{id}</code> does not exist.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Helper to show/hide on mobile based on tab. Desktop always shows.
  const showOn = (tab) =>
    `${activeTab === tab ? 'block' : 'hidden'} md:block`;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/leads"
          className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-accent"
        >
          <ArrowLeft size={14} /> Back to leads
        </Link>
        {apiReq.loading && <InlineSpinner />}
      </div>

      {apiReq.error && (
        <ErrorBanner
          message="Couldn’t refresh this lead from the server."
          onRetry={apiReq.refetch}
        />
      )}

      {/* Mobile tabs */}
      <div className="md:hidden -mx-1 overflow-x-auto">
        <div role="tablist" className="flex items-center gap-1 px-1 border-b border-border-card">
          {TABS.map((t) => {
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActiveTab(t.key)}
                className={`whitespace-nowrap px-3 py-2.5 text-sm font-medium border-b-2 -mb-px
                           transition-colors ${
                             active
                               ? 'text-accent border-accent'
                               : 'text-text-secondary border-transparent hover:text-text-primary'
                           }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Two-column layout (desktop) / tabbed stack (mobile) */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
        {/* Left column: 60% */}
        <div className="md:col-span-3 space-y-5">
          <div className={showOn('overview')}>
            <ContactHeader
              lead={effectiveLead}
              onAssign={handleAssign}
              canAssign={canAssign}
            />
          </div>
          <div className={showOn('verification')}>
            <LeadVerificationBrief lead={effectiveLead} />
          </div>
        </div>

        {/* Right column: 40% */}
        <div className="md:col-span-2 space-y-5">
          <div className={showOn('transcript')}>
            <ConversationPanel lead={effectiveLead} />
          </div>
          <div className={showOn('activity')}>
            <ActivityTimeline lead={effectiveLead} />
          </div>
          <div className={showOn('showings')}>
            <ShowingHistory lead={effectiveLead} />
          </div>
          <div className={showOn('overview')}>
            <NotesPanel lead={effectiveLead} />
          </div>
        </div>
      </div>
    </div>
  );
}
